import { useState, useCallback } from 'react';
import {
  useDownloadWriteoffDocumentMutation,
  useDeleteWriteoffDocumentMutation,
} from '../../../../../../services/WriteoffService';
import { WriteoffView } from '../../../../../../services/WriteoffService/types';
import { WriteoffDocumentView } from '../../../../../../services/WriteoffService/types/documentTypes';
import { useGetMyProfileQuery } from '../../../../../../services/ProfileService';

export interface UseDocumentActionsOptions {
  documentInfo: WriteoffDocumentView | null;
  writeoff: WriteoffView;
  onClose: () => void;
  onDocumentDeleted?: (documentId: string) => void;
}

export interface UseDocumentActionsReturn {
  isDownloading: boolean;
  isViewing: boolean;
  isDeleting: boolean;
  canDelete: boolean;
  handleDownload: () => Promise<void>;
  handleView: () => Promise<void>;
  handleDelete: () => Promise<void>;
}

export function useDocumentActions({
  documentInfo,
  writeoff,
  onClose,
  onDocumentDeleted,
}: UseDocumentActionsOptions): UseDocumentActionsReturn {
  const { data: userProfile } = useGetMyProfileQuery();
  const [downloadDocument] = useDownloadWriteoffDocumentMutation();
  const [deleteDocument] = useDeleteWriteoffDocumentMutation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = useCallback((): boolean => {
    if (!documentInfo || !userProfile) return false;
    const currentUserIdentity = userProfile.identity || userProfile.name;
    return documentInfo.uploadedBy === currentUserIdentity;
  }, [documentInfo, userProfile]);

  const handleDownload = useCallback(async () => {
    if (!documentInfo || !writeoff || isDownloading) return;

    setIsDownloading(true);
    try {
      const result = await downloadDocument({
        company: writeoff.identifiers.company,
        center: writeoff.identifiers.center,
        writeoffId: writeoff.identifiers.id,
        documentId: documentInfo.id,
      }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentInfo.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [documentInfo, writeoff, isDownloading, downloadDocument]);

  const handleView = useCallback(async () => {
    if (!documentInfo || !writeoff || isViewing) return;

    setIsViewing(true);
    try {
      const result = await downloadDocument({
        company: writeoff.identifiers.company,
        center: writeoff.identifiers.center,
        writeoffId: writeoff.identifiers.id,
        documentId: documentInfo.id,
      }).unwrap();

      // Open the document in a new tab
      const url = window.URL.createObjectURL(result);
      window.open(url, '_blank');
      // Clean up the URL after opening
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Failed to view document:', error);
      alert(`Failed to view document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsViewing(false);
    }
  }, [documentInfo, writeoff, isViewing, downloadDocument]);

  const handleDelete = useCallback(async () => {
    if (!documentInfo || !canDelete() || !writeoff || isDeleting) return;

    if (
      window.confirm(
        `Are you sure you want to delete "${documentInfo.fileName}"? This action cannot be undone.`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteDocument({
          company: writeoff.identifiers.company,
          center: writeoff.identifiers.center,
          writeoffId: writeoff.identifiers.id,
          documentId: documentInfo.id,
        }).unwrap();

        // Notify parent component about deletion
        onDocumentDeleted?.(documentInfo.id);
        onClose();
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  }, [documentInfo, canDelete, writeoff, isDeleting, deleteDocument, onDocumentDeleted, onClose]);

  return {
    isDownloading,
    isViewing,
    isDeleting,
    canDelete: canDelete(),
    handleDownload,
    handleView,
    handleDelete,
  };
}
